import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";
import {
  createAIConversation,
  deleteAIConversation,
  describeChatRequestError,
  patchAIConversation,
  streamAIConversationReply,
} from "@/lib/ai-chat/client";
import {
  ASSISTANT_CONVERSATION_KIND,
  type StoredConversationMessage,
} from "@/lib/ai-chat/types";
import { parseAIChatStreamLine, splitBufferedLines, toUserFacingErrorMessage } from "@/lib/ai-chat/stream-parser";
import {
  loadSessions,
  type Message,
  type ConversationSession,
} from "@/lib/ai-assistant-storage";

export type { Message, ConversationSession };

export type WidgetMode = "floating" | "sidebar";

export interface QuestionContext {
  questionText: string;
  difficulty: string;
  keyPoints?: string[];
  stepByStep?: string[];
  exampleAnswer?: string;
  sessionTopic: string;
  vocabulary?: Array<{ word: string; definition?: string }>;
}

/**
 * What the widget remembers about the page the reader is on.
 *
 * Only these two fields survive the fold into `ai-chat`, because they are the
 * only ones that changed a reply: `questionContext` drives the assistant's
 * "Current Practice Context" block, and `currentRoute` is the page it was
 * gathered on. `timeOnPage` and `userActions` used to ride along but never
 * reached any prompt — the old function accepted them and ignored them — and
 * the one consumer of a proactive suggestion never rendered one, so the
 * time-on-page interval and the whole proactive-suggestion path are gone.
 */
export interface UserContext {
  currentRoute: string;
  questionContext?: QuestionContext;
}

// Storage keys (per-key localStorage, preserved from the previous Context impl
// so existing user settings keep working — intentionally NOT zustand/persist,
// which would consolidate to a single key and drop saved settings).
const STORAGE_KEYS = {
  ASSISTANT_ENABLED: "lyder-ai-assistant-enabled",
  WIDGET_VISIBLE: "lyder-ai-assistant-widget-visible",
  WIDGET_MODE: "lyder-ai-assistant-widget-mode",
} as const;

// Constant for storage limits
const MAX_MESSAGES_PER_SESSION = 50;

// Helper to safely read from localStorage
function getStorageItem<T>(key: string, defaultValue: T): T {
  if (typeof window === "undefined") return defaultValue;
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
}

// Helper to safely write to localStorage (with optional debounce)
const storageTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};
function setStorageItem(key: string, value: unknown, debounceMs = 0): void {
  if (typeof window === "undefined") return;

  const performWrite = () => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      delete storageTimeouts[key];
    } catch (error) {
      console.warn("Failed to save to localStorage:", error);
    }
  };

  if (debounceMs > 0) {
    if (storageTimeouts[key]) clearTimeout(storageTimeouts[key]);
    storageTimeouts[key] = setTimeout(performWrite, debounceMs);
  } else {
    performWrite();
  }
}

// Helper to generate session title from messages
function getSessionTitle(messages: Message[]): string {
  const firstUserMessage = messages.find((msg) => msg.role === "user");
  if (firstUserMessage) {
    const title = firstUserMessage.content.trim().slice(0, 50);
    return title.length < firstUserMessage.content.trim().length
      ? `${title}...`
      : title;
  }
  return "New Conversation";
}

/**
 * Map the widget's turns onto the shared message store. The widget is plain
 * text, so every message persists as a single text part; the shared table wants
 * a timestamptz, which is the only real transformation.
 */
function toStoredMessages(messages: Message[]): StoredConversationMessage[] {
  return messages
    .filter((message) => message.content.trim() !== "")
    .map((message) => ({
      id: message.id,
      role: message.role,
      content: message.content,
      timestamp: new Date(message.timestamp).toISOString(),
      parts: [{ type: "text", text: message.content }],
    }));
}

// --- Module-level mutable refs (cannot live in reactive state) ---

// Abort controller for canceling ongoing streaming requests
let abortController: AbortController | null = null;

// Auth snapshot, synced by the provider from useAuth(). Read by async actions.
let authState: { isAuthenticated: boolean; userId: string | null } = {
  isAuthenticated: false,
  userId: null,
};

// Guard so DB sessions load only once per authenticated session.
let hasLoadedFromDB = false;

interface AIAssistantState {
  messages: Message[];
  sessions: ConversationSession[];
  currentSessionId: string | null;
  isOpen: boolean;
  isEnabled: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
  context: UserContext;
  widgetMode: WidgetMode;

  // Widget controls
  openWidget: () => void;
  closeWidget: () => void;
  toggleWidget: () => void;
  setEnabled: (enabled: boolean) => void;
  setWidgetMode: (mode: WidgetMode) => void;

  // Message actions
  sendMessage: (message: string, editFromIndex?: number) => Promise<void>;
  abortGeneration: () => void;
  clearConversation: () => void;

  // Session management
  createNewSession: () => void;
  switchSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => Promise<void>;
  renameSession: (sessionId: string, title: string) => Promise<void>;

  // Context actions
  updateContext: (updates: Partial<UserContext>) => void;

  // Provider-driven internals (not part of the public consumer surface)
  setAuth: (isAuthenticated: boolean, userId: string | null) => void;
  loadSessionsForUser: (userId: string) => Promise<void>;
  setRoute: (pathname: string) => void;
}

export const useAIAssistantStore = create<AIAssistantState>()((set, get) => ({
  messages: [],
  sessions: [],
  currentSessionId: null,
  isOpen: getStorageItem(STORAGE_KEYS.WIDGET_VISIBLE, false),
  isEnabled: getStorageItem(STORAGE_KEYS.ASSISTANT_ENABLED, true),
  isLoading: false,
  isAuthenticated: false,
  context: { currentRoute: "" },
  widgetMode: getStorageItem<WidgetMode>(STORAGE_KEYS.WIDGET_MODE, "floating"),

  // --- Widget controls ---
  openWidget: () => {
    set({ isOpen: true });
    setStorageItem(STORAGE_KEYS.WIDGET_VISIBLE, true);
  },
  closeWidget: () => {
    set({ isOpen: false });
    setStorageItem(STORAGE_KEYS.WIDGET_VISIBLE, false);
  },
  toggleWidget: () => {
    const newValue = !get().isOpen;
    set({ isOpen: newValue });
    setStorageItem(STORAGE_KEYS.WIDGET_VISIBLE, newValue);
  },
  setEnabled: (enabled: boolean) => {
    set({ isEnabled: enabled });
    setStorageItem(STORAGE_KEYS.ASSISTANT_ENABLED, enabled);
    if (!enabled) {
      set({ isOpen: false });
      setStorageItem(STORAGE_KEYS.WIDGET_VISIBLE, false);
    }
  },
  setWidgetMode: (mode: WidgetMode) => {
    set({ widgetMode: mode });
    setStorageItem(STORAGE_KEYS.WIDGET_MODE, mode);
  },

  // --- Session management ---
  createNewSession: () => {
    set({ currentSessionId: null, messages: [] });
  },
  switchSession: (sessionId: string) => {
    const session = get().sessions.find((s) => s.id === sessionId);
    if (session) {
      set({
        currentSessionId: sessionId,
        messages: session.messages.slice(-MAX_MESSAGES_PER_SESSION),
      });
    }
  },
  deleteSession: async (sessionId: string) => {
    const { isAuthenticated } = authState;
    if (isAuthenticated) {
      try {
        await deleteAIConversation(sessionId);
      } catch (error) {
        console.error("Error deleting session in DB:", error);
      }
    }
    const wasCurrent = sessionId === get().currentSessionId;
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== sessionId),
      ...(wasCurrent ? { currentSessionId: null, messages: [] } : {}),
    }));
  },
  renameSession: async (sessionId: string, title: string) => {
    const newTitle = title.trim();
    const { isAuthenticated } = authState;
    if (isAuthenticated && newTitle) {
      try {
        await patchAIConversation(sessionId, { title: newTitle });
      } catch (error) {
        console.error("Error renaming session in DB:", error);
      }
    }
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId
          ? { ...s, title: newTitle || getSessionTitle(s.messages) }
          : s
      ),
    }));
  },

  // --- Message actions ---
  abortGeneration: () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    set({ isLoading: false });
  },

  sendMessage: async (message: string, editFromIndex?: number) => {
    const s0 = get();
    if (!message.trim() || s0.isLoading) return;

    // The fold dropped the anonymous path: `ai-chat` requires a session, so a
    // signed-out visitor gets the panel's sign-in prompt and no request. There
    // is no anon fallback to keep the widget "working" while signed out.
    if (!authState.isAuthenticated || !authState.userId) {
      return;
    }

    if (abortController) abortController.abort();
    const controller = new AbortController();
    abortController = controller;

    const initialMessages = s0.messages;
    const wireHistory =
      editFromIndex !== undefined &&
      editFromIndex >= 0 &&
      editFromIndex < initialMessages.length
        ? initialMessages.slice(0, editFromIndex)
        : initialMessages;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: "user",
      content: message.trim(),
      timestamp: Date.now(),
    };
    const assistantMessage: Message = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "",
      timestamp: Date.now() + 1,
    };

    // Show the turn instantly: the user's message plus an empty assistant
    // bubble that the stream fills in.
    set({ messages: [...wireHistory, userMessage, assistantMessage], isLoading: true });

    let conversationId = s0.currentSessionId;
    let accumulatedContent = "";
    let streamError: string | null = null;

    try {
      if (!conversationId) {
        conversationId = await createAIConversation(ASSISTANT_CONVERSATION_KIND);
        const newSession: ConversationSession = {
          id: conversationId,
          title: getSessionTitle([userMessage]),
          messages: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
        };
        set((state) => ({
          currentSessionId: conversationId,
          sessions: [newSession, ...state.sessions],
        }));
        await patchAIConversation(conversationId, { title: newSession.title });
      }

      const response = await streamAIConversationReply(
        conversationId,
        // The new turn goes on the wire with the history it follows; the empty
        // assistant placeholder stays local until it has content.
        [...wireHistory, userMessage]
          .filter((msg) => msg.content.trim() !== "")
          .map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            parts: [{ type: "text" as const, text: msg.content }],
          })),
        controller.signal,
        false,
        {
          mode: "assistant",
          context: {
            currentRoute: s0.context.currentRoute,
            questionContext: s0.context.questionContext,
          },
        },
      );

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const split = splitBufferedLines(buffer, decoder.decode(value, { stream: true }));
        buffer = split.buffer;

        for (const line of split.lines) {
          for (const event of parseAIChatStreamLine(line)) {
            if (event.type === "text") {
              accumulatedContent += event.content;
              const content = accumulatedContent;
              set((state) => ({
                messages: state.messages.map((msg) =>
                  msg.id === assistantMessage.id ? { ...msg, content } : msg
                ),
              }));
            } else if (event.type === "error") {
              // The server maps its own failures to friendly copy; keep that
              // copy rather than the generic fallback below.
              streamError = toUserFacingErrorMessage(event.message);
            }
          }
        }
      }

      // A severed stream ends exactly like a clean one, so an empty body is
      // reported rather than left as a blank bubble.
      const finalContent =
        accumulatedContent ||
        (controller.signal.aborted
          ? "Generation cancelled."
          : streamError || "I'm sorry, I couldn't generate a response.");

      await finishTurn(conversationId, assistantMessage, finalContent);
    } catch (error) {
      const aborted =
        (error instanceof DOMException && error.name === "AbortError") ||
        controller.signal.aborted;

      if (aborted) {
        await finishTurn(
          conversationId,
          assistantMessage,
          accumulatedContent || "Generation cancelled.",
        );
      } else {
        // The stream parser already turned the server's friendly copy into
        // `streamError`; this path only renders the turn. A request that never
        // opened a stream still says why — same copy as the full-page chat, so
        // a quota or an expired session reads the same in both surfaces.
        console.error("Error sending message:", error);
        await finishTurn(
          conversationId,
          assistantMessage,
          streamError || describeChatRequestError(error),
        );
      }
    } finally {
      set({ isLoading: false });
      if (abortController === controller) {
        abortController = null;
      }
    }
  },

  clearConversation: () => {
    get().createNewSession();
  },

  // --- Context actions ---
  updateContext: (updates: Partial<UserContext>) => {
    set((state) => ({ context: { ...state.context, ...updates } }));
  },

  // --- Provider-driven internals ---
  setAuth: (isAuthenticated: boolean, userId: string | null) => {
    authState = { isAuthenticated, userId };
    set({ isAuthenticated });
    if (!isAuthenticated || !userId) hasLoadedFromDB = false;
  },
  loadSessionsForUser: async (userId: string) => {
    if (hasLoadedFromDB) return;
    hasLoadedFromDB = true;
    try {
      const dbSessions = await loadSessions(userId);
      if (dbSessions.length > 0) set({ sessions: dbSessions });
    } catch (error) {
      console.error(error);
    }
  },
  setRoute: (pathname: string) => {
    set((state) => ({ context: { ...state.context, currentRoute: pathname } }));
  },
}));

/**
 * Settle a finished turn: write the final assistant text into state and persist
 * the whole transcript (the shared store persists by replacing a conversation's
 * messages, not by appending one). The conversation id can be null when the
 * conversation could not be created, in which case the turn stays in memory.
 */
async function finishTurn(
  conversationId: string | null,
  assistantMessage: Message,
  finalContent: string,
): Promise<void> {
  const finalAssistantMessage: Message = { ...assistantMessage, content: finalContent };

  const store = useAIAssistantStore.getState();
  const finalMessages = store.messages
    .map((msg) => (msg.id === assistantMessage.id ? finalAssistantMessage : msg))
    .filter((msg) => msg.content.trim() !== "" || msg.id === assistantMessage.id);

  useAIAssistantStore.setState((state) => ({
    isLoading: false,
    messages: state.messages.map((msg) =>
      msg.id === assistantMessage.id ? finalAssistantMessage : msg
    ),
    sessions: state.sessions.map((session) =>
      session.id === conversationId
        ? {
            ...session,
            messages: finalMessages,
            updatedAt: Date.now(),
          }
        : session
    ),
  }));

  if (!conversationId) return;

  await patchAIConversation(conversationId, {
    messages: toStoredMessages(finalMessages),
  }).catch((error) => {
    console.error("Error saving conversation:", error);
  });
}

/**
 * Consumer hook with the same shape the old Context exposed. Selects the whole
 * store with a shallow equality check so components only re-render when a field
 * they use actually changes (actions are stable references).
 */
export function useAIAssistant() {
  return useAIAssistantStore(useShallow((s) => s));
}
